import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { db, primaryAuth } from '../firebase';
import { User } from '../types';
import { verifyPassword } from '../utils/passwordHash';
import { resolveLoginEmail, isEmail, syntheticEmailForUsername, writeAuthMap, writeLoginAlias } from '../utils/authAccounts';

interface AuthContextType {
  user: User | null;
  login: (usernameOrEmail: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'arztaleb_user_session';

const INVALID_CREDENTIALS_ERROR = 'Invalid username/email or password';
const ARCHIVED_ACCOUNT_ERROR = 'Bu hesap durdurulmuş. Lütfen yönetici ile iletişime geçin.';

/**
 * Resolve the users document for a signed-in Firebase Auth user.
 * New accounts have uid === document id; migrated legacy accounts are linked
 * through authMap/{uid} -> { userId }.
 */
const loadUserForFirebaseUser = async (fbUser: FirebaseUser): Promise<User | null> => {
  let userId = fbUser.uid;
  try {
    const mapDoc = await getDoc(doc(db, 'authMap', fbUser.uid));
    if (mapDoc.exists() && mapDoc.data().userId) {
      userId = mapDoc.data().userId as string;
    }
  } catch (e) {
    console.warn('[AUTH] authMap lookup failed, assuming uid == userId:', e);
  }
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) return null;
  const userData = { id: userDoc.id, ...userDoc.data() } as User;
  if (userData.archived === true) return null;
  return userData;
};

type MigrationResult = 'migrated' | 'exists' | 'skipped';

/**
 * Silently move a legacy (Firestore-password) user onto Firebase Auth using
 * the password they just typed and we just verified. Safe to fail: the caller
 * falls back to the legacy session, so login keeps working even before the
 * Email/Password provider is enabled in the Firebase console.
 */
const migrateLegacyUser = async (legacyUser: User, plainPassword: string): Promise<MigrationResult> => {
  if (!primaryAuth || !plainPassword) return 'skipped';
  const email = legacyUser.email && isEmail(legacyUser.email)
    ? legacyUser.email.trim().toLowerCase()
    : (legacyUser.username ? syntheticEmailForUsername(legacyUser.username) : null);
  if (!email) return 'skipped';
  try {
    const cred = await createUserWithEmailAndPassword(primaryAuth, email, plainPassword);
    try {
      await writeAuthMap(cred.user.uid, legacyUser.id, false);
    } catch (e) {
      console.error('[AUTH] Failed to write authMap during migration:', e);
    }
    if (legacyUser.username) {
      await writeLoginAlias(legacyUser.username, email, false);
    }
    try {
      // Remove the stored password and mark the account as migrated so the
      // legacy fallback can never be used to bypass Firebase Auth for it.
      await updateDoc(doc(db, 'users', legacyUser.id), {
        password: deleteField(),
        authEmail: email
      });
    } catch (e) {
      console.warn('[AUTH] Could not clean legacy password field:', e);
    }
    console.log('[AUTH] Legacy user migrated to Firebase Auth:', legacyUser.id);
    return 'migrated';
  } catch (e: any) {
    if (e?.code === 'auth/email-already-in-use') {
      return 'exists';
    }
    console.warn('[AUTH] Legacy user migration skipped:', e?.code || e);
    return 'skipped';
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Restore session: prefer a Firebase Auth session on the primary project,
  // fall back to the legacy localStorage session for not-yet-migrated users.
  useEffect(() => {
    console.log('[AUTH] Setting up session restore...', new Date().toISOString());

    const forceLoadingFalse = () => {
      console.warn('[AUTH] Received force-loading-false event, setting loading to false');
      setLoading(false);
    };
    window.addEventListener('force-loading-false', forceLoadingFalse);

    const timeoutId = setTimeout(() => {
      console.warn('[AUTH] Session load timeout after 5 seconds, setting loading to false');
      setLoading(false);
    }, 5000);

    const loadLegacySession = async () => {
      try {
        const storedSession = localStorage.getItem(STORAGE_KEY);
        if (!storedSession) {
          setUser(null);
          return;
        }
        const sessionData = JSON.parse(storedSession);
        try {
          const userDocRef = doc(db, 'users', sessionData.userId);
          const firestorePromise = getDoc(userDocRef);
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Firestore query timeout')), 3000)
          );
          const userDoc = await Promise.race([firestorePromise, timeoutPromise]) as any;
          if (userDoc.exists()) {
            const userData = { id: userDoc.id, ...userDoc.data() } as User;
            if (userData.archived === true) {
              localStorage.removeItem(STORAGE_KEY);
              setUser(null);
            } else {
              setUser(userData);
            }
          } else {
            localStorage.removeItem(STORAGE_KEY);
            setUser(null);
          }
        } catch (firestoreError: any) {
          const errorMessage = firestoreError?.message || '';
          const errorCode = firestoreError?.code || '';
          if (errorCode === 'permission-denied') {
            // Security rules are locked down: legacy sessions are no longer
            // valid. Send the user to the login screen, which migrates the
            // account to Firebase Auth on successful sign-in.
            console.warn('[AUTH] Legacy session rejected by security rules; login required');
            localStorage.removeItem(STORAGE_KEY);
            setUser(null);
          } else if (errorMessage.includes('timeout') ||
              errorCode === 'unavailable' ||
              errorCode === 'deadline-exceeded') {
            // Network problems: keep the stored session and let the next
            // successful connection sort it out.
            console.warn('[AUTH] Could not validate legacy session:', errorCode || errorMessage);
          } else {
            console.error('[AUTH] Error loading legacy session:', firestoreError);
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error('[AUTH] Error loading session:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    };

    let unsubscribe: (() => void) | undefined;
    if (primaryAuth) {
      unsubscribe = onAuthStateChanged(primaryAuth, async (fbUser) => {
        try {
          if (fbUser && !fbUser.isAnonymous) {
            const loaded = await loadUserForFirebaseUser(fbUser);
            if (loaded) {
              console.log('[AUTH] Firebase session restored for user:', loaded.id);
              setUser(loaded);
              localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId: loaded.id, timestamp: Date.now() }));
            } else {
              console.warn('[AUTH] Firebase user has no valid users document, signing out');
              await firebaseSignOut(primaryAuth);
              localStorage.removeItem(STORAGE_KEY);
              setUser(null);
            }
          } else {
            // No real Firebase session (visitor or anonymous): try legacy.
            await loadLegacySession();
          }
        } catch (e) {
          console.error('[AUTH] Session restore error:', e);
        } finally {
          clearTimeout(timeoutId);
          setLoading(false);
        }
      });
    } else {
      loadLegacySession().finally(() => {
        clearTimeout(timeoutId);
        setLoading(false);
      });
    }

    return () => {
      window.removeEventListener('force-loading-false', forceLoadingFalse);
      clearTimeout(timeoutId);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const login = useCallback(async (usernameOrEmail: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true);

      if (!usernameOrEmail.trim() || !password.trim()) {
        return { success: false, error: 'Please enter both username/email and password' };
      }

      const trimmedPassword = password.trim();

      // ------------------------------------------------------------------
      // 1) Firebase Auth (primary project) — the secure path.
      // ------------------------------------------------------------------
      if (primaryAuth) {
        try {
          const email = await resolveLoginEmail(usernameOrEmail);
          const cred = await signInWithEmailAndPassword(primaryAuth, email, trimmedPassword);
          const loaded = await loadUserForFirebaseUser(cred.user);
          if (!loaded) {
            await firebaseSignOut(primaryAuth);
            return { success: false, error: ARCHIVED_ACCOUNT_ERROR };
          }
          setUser(loaded);
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId: loaded.id, timestamp: Date.now() }));
          return { success: true };
        } catch (fbError: any) {
          const code = fbError?.code || '';
          console.log('[AUTH] Firebase sign-in did not succeed (' + code + '), trying legacy login');
          // Fall through to the legacy path for every failure: unmigrated
          // users, provider not yet enabled, etc. The legacy path re-verifies
          // the password itself, so this is not a bypass.
        }
      }

      // ------------------------------------------------------------------
      // 2) Legacy fallback: verify against the Firestore users collection.
      //    Works only until the security rules are locked down; users are
      //    silently migrated to Firebase Auth on successful legacy login.
      // ------------------------------------------------------------------
      let snapshot;
      try {
        const usersRef = collection(db, 'users');
        snapshot = await getDocs(usersRef);
      } catch (firestoreError: any) {
        const errorCode = firestoreError?.code || '';
        if (errorCode === 'permission-denied') {
          // Rules are locked and this account was never migrated.
          return { success: false, error: INVALID_CREDENTIALS_ERROR };
        }
        return {
          success: false,
          error: 'Unable to connect to database. Please check your internet connection and try again.'
        };
      }

      let foundUser: User | null = null;
      const searchTerm = usernameOrEmail.toLowerCase().trim();
      snapshot.docs.forEach(docSnap => {
        const userData = { id: docSnap.id, ...docSnap.data() } as User;
        const userEmail = userData.email?.toLowerCase() || '';
        const userUsername = userData.username?.toLowerCase() || '';
        if (userEmail === searchTerm || userUsername === searchTerm) {
          foundUser = userData;
        }
      });

      if (!foundUser) {
        return { success: false, error: INVALID_CREDENTIALS_ERROR };
      }

      const legacyUser = foundUser as User;

      if (legacyUser.archived === true) {
        return { success: false, error: ARCHIVED_ACCOUNT_ERROR };
      }

      // Accounts already migrated to Firebase Auth must never authenticate
      // through the legacy path (their Firestore password has been removed).
      if ((legacyUser as any).authEmail) {
        return { success: false, error: INVALID_CREDENTIALS_ERROR };
      }

      let isValid = false;
      if (!legacyUser.password) {
        // Backward compatibility: legacy accounts without a stored password.
        console.warn('[AUTH] Legacy user has no password set');
        isValid = true;
      } else {
        const trimmedStoredPassword = legacyUser.password.trim();
        if (trimmedPassword === trimmedStoredPassword) {
          isValid = true;
        } else {
          const looksLikeHash = trimmedStoredPassword.length >= 40 &&
                                /^[A-Za-z0-9+/=]+$/.test(trimmedStoredPassword);
          if (looksLikeHash) {
            try {
              if (typeof crypto !== 'undefined' && crypto.subtle) {
                isValid = await verifyPassword(trimmedPassword, trimmedStoredPassword);
              } else {
                return {
                  success: false,
                  error: 'Password verification requires a secure connection (HTTPS). Please contact support if this issue persists.'
                };
              }
            } catch (hashError: any) {
              console.error('[AUTH] Error verifying hashed password:', hashError);
              isValid = false;
            }
          }
        }
      }

      if (!isValid) {
        return { success: false, error: INVALID_CREDENTIALS_ERROR };
      }

      // Legacy credentials verified — migrate to Firebase Auth on the fly.
      const migration = await migrateLegacyUser(legacyUser, trimmedPassword);
      if (migration === 'exists') {
        // An Auth account already owns this email and the typed password does
        // not match it. Do not allow the legacy data to override Firebase Auth.
        return { success: false, error: INVALID_CREDENTIALS_ERROR };
      }

      setUser(legacyUser);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId: legacyUser.id, timestamp: Date.now() }));
      return { success: true };
    } catch (error: any) {
      console.error('Error logging in:', error);
      const errorMessage = error?.message || '';
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        return {
          success: false,
          error: 'Network connection error. Please check your internet connection and try again.'
        };
      }
      return { success: false, error: error.message || 'An error occurred during login' };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    if (primaryAuth) {
      firebaseSignOut(primaryAuth).catch(e => console.warn('[AUTH] Firebase sign-out failed:', e));
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const fbUser = primaryAuth?.currentUser;
      if (fbUser && !fbUser.isAnonymous) {
        const loaded = await loadUserForFirebaseUser(fbUser);
        if (loaded) {
          setUser(loaded);
        } else {
          localStorage.removeItem(STORAGE_KEY);
          setUser(null);
          await firebaseSignOut(primaryAuth!);
        }
        return;
      }

      const storedSession = localStorage.getItem(STORAGE_KEY);
      if (storedSession) {
        const sessionData = JSON.parse(storedSession);
        try {
          const userDocRef = doc(db, 'users', sessionData.userId);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = { id: userDoc.id, ...userDoc.data() } as User;
            if (userData.archived === true) {
              localStorage.removeItem(STORAGE_KEY);
              setUser(null);
            } else {
              setUser(userData);
            }
          } else {
            localStorage.removeItem(STORAGE_KEY);
            setUser(null);
          }
        } catch (firestoreError: any) {
          const errorCode = firestoreError?.code || '';
          if (errorCode === 'unavailable' || errorCode === 'deadline-exceeded' || errorCode === 'permission-denied') {
            console.warn('Could not refresh user (keeping current state):', errorCode);
          } else {
            console.error('Error refreshing user:', firestoreError);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      refreshUser,
      loading,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
